import React from "react";
import {
  Users,
  Heart,
  Star,
  ArrowRight,
  Target,
  Medal,
  Globe,
  Briefcase,
} from "lucide-react";

export interface CoreValueType {
  icon: React.ReactNode;
  title: string;
  description: string[];
}

export interface AboutContent {
  heroContent: {
    title: string;
    subtitle: string;
  };
  storyContent: {
    title: string;
    paragraphs: string[];
    coreMessages: string[];
    image: string;
  };
  visionContent: {
    title: string;
    points: string[];
    additionalText: string;
    goals: string[];
    image: string;
  };
  missionContent: {
    title: string;
    text: string;
    slogan: string;
    sloganText: string;
  };
  coreValues: CoreValueType[];
}

export const useAbout = (): AboutContent => {
  // Hero section content
  const heroContent = {
    title: "The Story of GlowHer.Life",
    subtitle:
      "Every woman has a light within her – she just needs to know how to let it shine.",
  };

  // Story section content
  const storyContent = {
    title: "📖 The Story of GlowHer.Life",
    paragraphs: [
      '"Every woman has a light within her – she just needs to know how to let it shine." One late evening, a woman sat in front of her laptop, holding a warm cup of tea, eyes filled with contemplation. She had just gone through a long day—work deadlines, family responsibilities, and in the midst of it all, she felt like she had forgotten herself.',
      '💬 "Why do women always have to choose between career, family, and themselves? Why can\'t we have it all—balanced and fulfilling?"',
      "She realized that modern women don't lack opportunities—but they need a space to learn how to optimize life in a smart, elegant, and fulfilling way.",
      "🌟 And so, GlowHer.Life was born – a platform to help women not only look beautiful but also live smart.",
      "GlowHer.Life is not just a website—it is a journey. A journey of discovering the best version of yourself.",
    ],
    coreMessages: [
      "🔹 You don't need to chase every trend—just learn to select what truly suits you.",
      "🔹 You don't have to sacrifice between work, family, and self-care—balance is the key.",
      "🔹 You don't have to shine according to society's standards—just be the most radiant version of yourself.",
      "🌍 From a small idea, GlowHer.Life has grown into a thriving community where thousands of women connect, learn, and inspire one another.",
      "🚀 Let's build a generation of modern, intelligent, and elegant women together—because you deserve to live a life that truly shines!",
    ],
    image:
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1800&q=80",
  };

  // Vision section content
  const visionContent = {
    title: "🌟 Vision of GlowHer.Life",
    points: [
      '"To become the leading platform that empowers modern women to build a smart, elegant, and fulfilling life."',
      "GlowHer.Life envisions a world where women can achieve success in every aspect of their lives—without compromise.",
      "We believe that true empowerment comes from knowledge, confidence, and the ability to make informed choices.",
    ],
    additionalText:
      "By providing high-quality content, resources, and a supportive community, we strive to help women live smarter, feel more confident, and embrace their elegance every day.",
    goals: [
      "✅ Build a comprehensive ecosystem of content covering lifestyle, beauty, health, and smart consumerism.",
      "✅ Create a strong and supportive community where women can connect, share, and inspire each other.",
      "✅ Integrate tools and valuable resources to help women make informed, intelligent decisions in shopping, self-care, and life management.",
      "✅ Become a trusted partner of top global brands, bringing quality products and services to women worldwide.",
    ],
    image:
      "https://images.unsplash.com/photo-1543269865-cbf427effbad?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1800&q=80",
  };

  // Mission section content
  const missionContent = {
    title: "Our Mission",
    text: "GlowHer.Life is more than just an online platform—it is a dedicated space for modern, intelligent, and stylish women. We provide high-quality content on beauty, fashion, health, and lifestyle, empowering you to shine in your own unique way.",
    slogan: "🌿 Live Smart – Live Elegantly 🌿",
    sloganText:
      "GlowHer.Life was created with the mission to inspire and provide optimal solutions for women to achieve financial independence, take care of themselves and their families, and fully enjoy life.",
  };

  // Core values section content
  const coreValues: CoreValueType[] = [
    {
      icon: <Heart className="h-8 w-8" />,
      title: "Empowerment – Unlocking Your Full Potential",
      description: [
        "We believe that every woman has the power to shape her own destiny.",
        "GlowHer.Life provides content that helps you become more confident, resilient, and proactive in every decision.",
      ],
    },
    {
      icon: <Star className="h-8 w-8" />,
      title: "Elegance – Beauty Inside and Out",
      description: [
        "True elegance goes beyond appearances—it's a state of mind.",
        "We celebrate the inner and outer beauty of women, focusing on sustainable practices that make you feel good about yourself.",
      ],
    },
    {
      icon: <Target className="h-8 w-8" />,
      title: "Smart Living – Making Informed Choices",
      description: [
        "Life is a series of choices—we help you make the smart ones.",
        "From financial decisions to self-care practices, GlowHer.Life guides you toward choices that enhance your quality of life.",
      ],
    },
  ];

  return {
    heroContent,
    storyContent,
    visionContent,
    missionContent,
    coreValues,
  };
};
